import { useAuth0 } from '@auth0/auth0-react';
import type { AlertColor } from '@mui/material';
import { useErrorBoundary } from 'react-error-boundary';

import type { ConfirmMessage } from '@/models/ConfirmMessage';
import { setAxiosTokenInterceptor } from '@/services/interceptor';
import { LocalStorageService } from '@/services/local-storage';

import useConfirm from '../hooks/useConfirm';
import usePrompt from '../hooks/usePrompt';
import type { OU } from '../models/Ou';
import { useAppDispatch } from '../store';
import { getCurrentOu, SetSnackbarMessage, updateOu } from '../store/slices/application-data';
import { fetchFuelUserAccess } from '../store/slices/fuelUserAccess';
import { Save } from '../store/slices/user';

function useAppUtils() {
  const { fuelConfirm, vnConfirmFn } = useConfirm();
  const { fuelPrompt } = usePrompt();
  const { showBoundary } = useErrorBoundary();

  const { user, getAccessTokenSilently, getIdTokenClaims, isAuthenticated, logout } = useAuth0();
  const dispatch = useAppDispatch();
  const alert = (msg: string, severity: AlertColor = 'success', details = null) => {
    dispatch(SetSnackbarMessage({ message: msg, severity, details }));
  };

  const confirm = async (msg: string) => {
    return fuelConfirm(msg);
  };

  const vnConfirm = async (msg: string | ConfirmMessage) => {
    return vnConfirmFn(msg);
  };

  const prompt = async (msg: string) => {
    return fuelPrompt(msg);
  };

  const getAppResources = async (argFuelUserAccessData, argUserDetails, argPermissions, argOuData) => {
    let ouDataNew: OU = argOuData;
    let permissionsNew = argPermissions;
    let userDetailsNew = argUserDetails;
    let fuelUserAccessDataNew = argFuelUserAccessData;

    // The pattern for app loading is to get the user infor and then load the user access data and ou info
    if (user && isAuthenticated) {
      // console.log('getAppResources getting access token', { user, isAuthenticated });
      try {
        const accessToken = await getAccessTokenSilently();
        const newTokenresponse = await getIdTokenClaims();
        userDetailsNew = {
          ...user,
          id_token: newTokenresponse?.__raw,
          access_token: accessToken,
          expires_at: newTokenresponse?.exp,
        };

        // save the tokens to localstorage
        if (accessToken) {
          LocalStorageService.setItem('ACCESS_TOKEN', accessToken);
          const nextAccessTokenRenewTime = new Date(Date.now() + 1000 * 60 * 3).getTime(); // renew every 3 minutes
          LocalStorageService.setItem('ACCESS_TOKEN_EXPIRE', nextAccessTokenRenewTime);
          LocalStorageService.setItem('userInfo', user);
          await setAxiosTokenInterceptor(alert, getAccessTokenSilently, showBoundary, logout);
        }

        dispatch(Save(userDetailsNew));
      } catch (err) {
        console.error('Unable to get access token', err);
        alert('Unable to get access token.  Please refresh your app, and contact support if the operation persists.');
      }
    }

    if (!permissionsNew && userDetailsNew) {
      // console.log('getAppResources getting user access', { user, isAuthenticated });
      const userAccessData = await dispatch(fetchFuelUserAccess(userDetailsNew));
      fuelUserAccessDataNew = userAccessData;
      permissionsNew = userAccessData.payload;
    }

    if (!ouDataNew && fuelUserAccessDataNew) {
      // console.log('getAppResources getting ouData', { user, isAuthenticated });
      const getCurrentOUResults = await dispatch(getCurrentOu(true));
      if (getCurrentOUResults && getCurrentOUResults.payload) {
        updateOu({ ouId: getCurrentOUResults.payload.id, forceUpdate: false });
        ouDataNew = getCurrentOUResults.payload;
      }
    }

    return { fuelUserAccessDataNew, userDetailsNew, permissionsNew, ouDataNew };
  };

  return { confirm, prompt, alert, getAppResources, vnConfirm };
}

export default useAppUtils;
